import { getMemoizationItem, MemoizationState } from "./memoization-store";

/**
 * @param selectorArgs - parameters for selector function, used for selecting specific part of state
 *
 * IMPORTANT: do not use `selectorArgs` in nested selectors
 *
 * @example
 * 	\@memoizeSelector(state => [
 * 		state.selection // selector will trigger for change in any selection
 * 	])
 * 	getSelection(id: string) {
 * 		return (state) => {...state.selection[id]};
 * 	}
 *
 * 	\@memoizeSelector((state, selectorArgs) => [
 * 		state.selection[selectorArgs[0]] // selector will trigger for change in specific selection only
 * 	])
 * 	getSelection(id: string) { // selectorArgs[0] above is value of id parameter here
 * 		return (state) => {...state.selection[id]};
 * 	}
 *
 * 	// note: new object creation (=> {...state.sel..) is used for example only, in real world that would be some mapping function
 */
export type SelectorInputs<T> = (state: T, selectorArgs: IArguments) => any[];

// eslint-disable-next-line @typescript-eslint/ban-types
export type SelectorFactory<T> = Function & {
	selectorInputs?: SelectorInputs<T>;
	nestedSelectors?: () => (() => unknown)[];
};

/**
 * Makes sure that selector logic is executed only when specified inputs (state parts) change.
 * It stores last values of inputs, on subsequent calls checks if they're changed, returns cached result if not.
 * Works similar to rxjs `distinctUntilChanged` with shallow compare of inputs.
 *
 * Used for minimizing execution time of complex selector logic. Supports nested selector inputs.
 *
 * Memoization can be disabled for selectors that don't require it (e.g. selectors that are not used directly in UI,
 * but are nested in other, memoized selectors). With memoization disabled, selector inputs will be still reused.
 *
 * Memoization should NOT be used for selectors with simple logic not returning new object instance.
 *
 * @example
 * 	// memoization is not needed:
 * 	getSelection(id: string) { // for each id returns same reference, won't trigger change detection on ui
 * 		return (state) => state.selection[id];*
 * 	}
 *
 * 	// memoization is required:
 * 	\@memoizeSelector(state => [
 * 		state.selection
 * 	])
 * 	getMappedSelection(id: string) { // returns new object instance every time, eventually triggering change detection in ui
 * 		return (state) => ({
 * 			...state.selection[id],
 * 			newField: true
 * 		});
 * 	}
 */
export function memoizeSelector<TState>(
	inputs: SelectorInputs<TState> | undefined,
	nestedSelectors: () => ((...args: unknown[]) => unknown)[] = () => [],
	enableMemoization = true
) {
	return (_target: any, _name: string, descriptor: PropertyDescriptor) => {
		const originalFunction = descriptor.value;

		if (!enableMemoization) {
			// attach selector metadata
			originalFunction.selectorInputs = inputs;
			originalFunction.nestedSelectors = nestedSelectors;
			return;
		}

		// handle memoization
		let resolvedInputs: SelectorInputs<TState>;

		const wrapper = function () {
			// eslint-disable-next-line @typescript-eslint/no-this-alias,no-invalid-this
			const originalContext = this;
			const originalArguments = arguments;
			const argsKey = JSON.stringify(arguments);

			resolvedInputs = resolvedInputs || resolveAllInputs(wrapper);

			// note: this will be called with every state change
			return (state: TState & { __memoize: Map<() => unknown, Record<string, MemoizationState>> }) => {
				const memoizeStore = typeof window === "undefined" ? state.__memoize : undefined;
				const memoizationItem = getMemoizationItem(originalFunction, argsKey, memoizeStore);
				let hasChanged = memoizationItem.stateSnapshot.length === 0;

				// get array of parts of state that selector depends upon
				const dependencyValues = resolvedInputs(state, originalArguments);

				for (let j = 0; j < dependencyValues.length; ++j) {
					// check if selector input changed from the previous call
					hasChanged = hasChanged || memoizationItem.stateSnapshot[j] !== dependencyValues[j];
					// save selector input value
					memoizationItem.stateSnapshot[j] = dependencyValues[j];
				}

				if (hasChanged) {
					// eslint-disable-next-line no-invalid-this
					memoizationItem.cachedReturnValue = originalFunction.apply(
						originalContext,
						originalArguments
					)(state);
				}

				return memoizationItem.cachedReturnValue;
			};
		} as SelectorFactory<TState>;

		wrapper.selectorInputs = inputs;
		wrapper.nestedSelectors = nestedSelectors;

		descriptor.value = wrapper;
	};
}

/**
 * Combines direct and nested selector inputs including all descendants
 */
function resolveAllInputs<T>(wrapper: SelectorFactory<T>, inputs: SelectorInputs<T>[] = []): SelectorInputs<T> {
	if (wrapper.selectorInputs) {
		inputs.push(wrapper.selectorInputs);
	}
	if (wrapper.nestedSelectors) {
		for (const nestedSelector of wrapper.nestedSelectors()) {
			resolveAllInputs(nestedSelector, inputs);
		}
	}
	return combine(inputs);
}

/**
 * Combines multiple selector input functions into one, returning shallow array of inputs
 */
function combine<T>(inputs: SelectorInputs<T>[]): SelectorInputs<T> {
	return (state: T, selectorArgs: IArguments) => {
		let result: any[] = [];

		for (const input of inputs) {
			result = result.concat(input(state, selectorArgs));
		}

		return result;
	};
}
